import {useLingui} from '@lingui/react/macro';
import {Svg, Path, Defs, LinearGradient, Stop, Rect} from 'react-native-svg';
import {StyleSheet, View, Text, Pressable} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {useCallback, createRef, useState, Fragment} from 'react';
import {useMenu} from 'extensions/viewport/useMenu';
import {usePopup} from 'extensions/viewport/usePopup';
import {IconButton} from 'interface/base/IconButton';
import {JotaryLogomark} from 'interface/base/JotaryLogo';
import {useModalJotary} from 'interface/hooks/useModalJotary';
import {useModalStorageUpsell} from 'interface/hooks/useModalStorageUpsell';
import {toggleStorageBannerTest} from 'interface/hooks/useStorageBannerTest';
import {useDebugMenuRevealed} from 'interface/hooks/useDebugMenu';
import {isTouch, isUpsellDeniedPlatform, isSandbox, isNative} from 'utils/platform';
import {openPage, destroySession, switchEnvironment} from 'utils/mediafire';
import {colors, vectors} from 'config/styles';

import * as app from 'store/slices/app';

export function MenuLinks() {
  const {t} = useLingui();
  const isPremium = useSelector(app.selectors.isPremium);
  const email = useSelector(app.selectors.getEmail);
  const classes = {
    button: [
      styles.button,
      isTouch() && styles.buttonTouch,
    ]
  };

  const jotary = useModalJotary();
  const [pill, setPill] = useState({width: 0, height: 0});

  const dispatch = useDispatch();
  const logout = useCallback(() => destroySession(dispatch), []);
  const gotoHelp = useCallback(() => openPage('help/submit_a_ticket.php'), []);
  const gotoDiscord = useCallback(() => openPage('https://discord.gg/wpNru5GaCe'), []);
  const gotoAccount = useCallback(() => openPage('myaccount/'), []);
  const gotoEarnSpace = useCallback(() => openPage('earnspace/'), []);

  const popup = usePopup();
  const menuRef = createRef<any>();
  const [showStorageUpsellRemind] = useModalStorageUpsell('remind');
  const [showStorageUpsellUpload] = useModalStorageUpsell('upload');
  const [showStorageUpsellManage] = useModalStorageUpsell('manage');
  const debugRevealed = useDebugMenuRevealed();
  const menuMoreLinks = useMenu(() =>
    [
      {
        command: 'header',
        text: t`More Links`,
      },
      {
        command: 'about',
        text: t`About`,
      },
      {
        command: 'policies',
        text: t`Policies`,
      },
      {
        command: 'privacy',
        text: t`Privacy`,
      },
      {
        command: 'abuse',
        text: t`Abuse`,
      },
      {
        command: 'help',
        text: t`Help`,
      },
      ...(debugRevealed ? [
        {
          command: 'storage-upsell-remind-test',
          text: t`Storage Upsell: Remind (test)`,
        },
        {
          command: 'storage-upsell-upload-test',
          text: t`Storage Upsell: Upload (test)`,
        },
        {
          command: 'storage-upsell-manage-test',
          text: t`Storage Upsell: Manage (test)`,
        },
        {
          command: 'storage-banner-over-test',
          text: t`Storage Banner Over (test)`,
        },
        {
          command: 'storage-banner-critical-test',
          text: t`Storage Banner Critical (test)`,
        },
        {
          command: 'storage-banner-warning-test',
          text: t`Storage Banner Warning (test)`,
        },
        ...(isNative() ? [{
          command: 'switch-environment',
          text: isSandbox()
            ? t`Environment: Sandbox → Live`
            : t`Environment: Live → Sandbox`,
        }] : []),
      ] : []),
    ],
    (command) => {
      switch (command) {
        case 'about':
          openPage('/about/');
          break;
        case 'policies':
          openPage('/policies/terms_of_service.php');
          break;
        case 'privacy':
          openPage('/policies/privacy_policy.php');
          break;
        case 'abuse':
          openPage('/policy_violation/terms_of_service.php');
          break;
        case 'help':
          openPage('/help/submit_a_ticket.php');
          break;
        case 'storage-upsell-remind-test':
          showStorageUpsellRemind();
          break;
        case 'storage-upsell-upload-test':
          showStorageUpsellUpload();
          break;
        case 'storage-upsell-manage-test':
          showStorageUpsellManage(1024 * 1024 * 1024);
          break;
        case 'storage-banner-over-test':
          toggleStorageBannerTest('over');
          break;
        case 'storage-banner-critical-test':
          toggleStorageBannerTest('critical');
          break;
        case 'storage-banner-warning-test':
          toggleStorageBannerTest('warning');
          break;
        case 'switch-environment':
          switchEnvironment();
          break;
      }
      menuMoreLinks.exit();
    }
  , false);

  const showMoreMenu = () => {
    if (popup) {
      popup.setPosition(undefined);
      popup.setRef(menuRef);
    }
    menuMoreLinks.open();
  }

  return (
    <Fragment>
      <View style={styles.item}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t`Open Jotary`}
          onPress={jotary.open}
          /* @ts-ignore - hovered is web-only */
          style={({hovered}: any) => [
            styles.button,
            isTouch() && styles.buttonTouch,
            hovered && styles.buttonHover,
          ]}>
          <View style={styles.jotaryIcon}>
            <JotaryLogomark size={20}/>
          </View>
          <Text style={[styles.text, styles.jotaryText]}>
            {t`Jotary`.toUpperCase()}
          </Text>
          <View
            style={styles.pill}
            onLayout={(e) => setPill({
              width: e.nativeEvent.layout.width,
              height: e.nativeEvent.layout.height,
            })}>
            {pill.width > 0 &&
              <Svg width={pill.width} height={pill.height} style={StyleSheet.absoluteFill}>
                <Defs>
                  <LinearGradient id="jotaryPill" x1="0" y1="0" x2={pill.width} y2={pill.height} gradientUnits="userSpaceOnUse">
                    <Stop offset="0" stopColor="#f97316"/>
                    <Stop offset="1" stopColor="#ec4899"/>
                  </LinearGradient>
                </Defs>
                <Rect width={pill.width} height={pill.height} rx={pill.height / 2} fill="url(#jotaryPill)"/>
              </Svg>
            }
            <Text style={styles.pillText}>{t`New`.toUpperCase()}</Text>
          </View>
        </Pressable>
      </View>
      {!isPremium && !isUpsellDeniedPlatform() &&
        <View style={styles.item}>
          <IconButton
            onPress={gotoEarnSpace}
            title={t`Earn space`}
            text={t`Earn space`.toUpperCase()}
            textStyle={styles.text}
            buttonStyle={classes.button}
            buttonHoverStyle={styles.buttonHover}
            icon={
              <Svg viewBox="0 0 20 20" width={20} height={20}>
                <Path d={vectors.icons.plus} fill={colors.neutral.mid}/>
              </Svg>
            }
          />
        </View>
      }
      {email &&
        <View style={styles.item}>
          <IconButton
            onPress={gotoAccount}
            title={t`Open account settings`}
            text={t`Account`.toUpperCase()}
            textStyle={styles.text}
            buttonStyle={classes.button}
            buttonHoverStyle={styles.buttonHover}
            icon={
              <Svg viewBox="0 0 20 20" width={20} height={20}>
                <Path d={vectors.icons.gear} fill={colors.neutral.mid}/>
              </Svg>
            }
          />
        </View>
      }
      <View style={styles.item}>
        <IconButton
          onPress={logout}
          title={t`Logout`}
          text={t`Logout`.toUpperCase()}
          textStyle={styles.text}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path d={vectors.icons.exit} fill={colors.neutral.mid} />
            </Svg>
          }/>
      </View>
      <View style={styles.item}>
        <IconButton
          onPress={gotoHelp}
          title={t`Open support ticket page`}
          text={t`Help`.toUpperCase()}
          textStyle={styles.text}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path d={vectors.icons.help} fill={colors.neutral.mid} />
            </Svg>
          }/>
      </View>
      <View style={styles.item}>
        <IconButton
          onPress={gotoDiscord}
          title={t`Open discord invite`}
          text={'DISCORD'}
          textStyle={styles.text}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 126.644 96" width={20} height={20}>
              <Path d={vectors.icons.discord} fill={colors.neutral.mid} />
            </Svg>
          }/>
      </View>
      <View style={styles.item} ref={menuRef}>
        <IconButton
          reference={menuMoreLinks.reference}
          onPress={showMoreMenu}
          title={t`More links`}
          text={t`More`.toUpperCase()}
          textStyle={styles.text}
          iconStyle={styles.iconMore}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path
                d={vectors.icons.moreHoriz}
                fill={colors.neutral.mid}/>
            </Svg>
          }
        />
      </View>
    </Fragment>
  );
};

const styles = StyleSheet.create({
  iconMore: {
    marginTop: 16,
  },
  text: {
    color: colors.neutral.mid,
    fontSize: 12,
  },
  item: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 11,
    marginRight: 8,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: 10,
    lineHeight: 36,
    height: 36,
    width: 178,
    borderRadius: 0,
    color: colors.neutral.mid,
  },
  buttonHover: {
    backgroundColor: colors.neutral.darker,
  },
  buttonTouch: {
    lineHeight: 40,
    height: 40,
  },
  jotaryIcon: {
    width: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  jotaryText: {
    flex: 1,
    marginLeft: 10,
  },
  pill: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 999,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f97316',
  },
  pillText: {
    color: colors.neutral.white,
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
});
